import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { api } from "../../../singleton/axios";
import { NotificationItem } from "../types";
import {
  NOTIFICATION_UNAVAILABLE,
  isNotificationUnavailableError,
  notificationItemQueryKey,
} from "../notificationUnavailable";

/**
 * One notification, for the /n/:id link route. A plain GET that marks nothing
 * read — mail scanners and link previews fetch URLs. `null` means the id is
 * not this user's, was pruned, or is not an id at all (the server answers 404
 * for all three); the link route then opens the inbox. NOTIFICATION_UNAVAILABLE
 * means it is this user's but was withdrawn (410): the route shows a neutral
 * "no longer available" state instead.
 */
export const useNotification = (id: string | undefined) =>
  useQuery({
    queryKey: notificationItemQueryKey(id),
    queryFn: async (): Promise<NotificationItem | typeof NOTIFICATION_UNAVAILABLE | null> => {
      try {
        const { data } = await api.get<{ item: NotificationItem }>(
          `/notifications/${encodeURIComponent(id ?? "")}`,
        );
        return data.item;
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) return null;
        if (isNotificationUnavailableError(error)) return NOTIFICATION_UNAVAILABLE;
        throw error;
      }
    },
    enabled: Boolean(id),
    staleTime: 0,
    retry: false,
  });
